// Web Push en el front: soporte, permiso, suscripción con VAPID y registro en el backend.
// Spec: docs/2026-08-25-push-inactividad-pwa-spec.md
//
// OJO dev: el SW se DESREGISTRA en `npm run dev` (main.tsx) → `serviceWorker.ready` no resuelve.
// El testing de push se hace con un build servido (vite preview) o en producción.

import { VAPID_PUBLIC_KEY } from '../config';
import { atlasBackendFetch } from './atlasBackend';
import { supabase } from './supabase';

/** VAPID public key (base64url) → Uint8Array, como exige pushManager.subscribe. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export type PushSoporte =
  | 'ok' // se puede pedir/usar push
  | 'sin-soporte' // el navegador no tiene SW/Push/Notification
  | 'ios-no-instalada'; // iOS pero la PWA no está instalada → no hay push posible

/** ¿Este navegador/dispositivo puede recibir push ahora? */
export function soportePush(): PushSoporte {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'sin-soporte';
  }
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  if (isIOS && !standalone) return 'ios-no-instalada';
  return 'ok';
}

/** Estado actual del permiso del navegador. */
export function permisoPush(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Devuelve el `ServiceWorkerRegistration` activo, o null si no hay SW usable.
 *
 * Clave: primero preguntamos si HAY un registro (`getRegistration`). En `npm run dev` el SW se
 * DESREGISTRA al arrancar (main.tsx) → no hay registro → devolvemos null sin esperar `ready` (que
 * nunca resolvería y colgaría la UI). En producción SÍ hay registro → esperamos a que active con
 * `ready`. NO usamos un timeout corto: una primera instalación/actualización lenta (>3s) no debe
 * falsear "no disponible". El timeout largo es solo un cinturón anti-cuelgue por si un SW registrado
 * jamás llegara a activar (caso patológico).
 */
async function swReady(timeoutMs = 20000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const existente = await navigator.serviceWorker.getRegistration().catch(() => null);
  if (!existente) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

export interface ActivarResultado {
  ok: boolean;
  /** 'sin-soporte' | 'ios-no-instalada' | 'permiso-denegado' | 'sw-no-disponible' | 'backend-<status>' | 'cancelado' (se desactivó mientras tanto) | 'error' */
  motivo?: string;
}

/**
 * Pide permiso (si hace falta), suscribe con VAPID y registra la subscription en el backend.
 * DEBE llamarse desde un gesto del usuario (click). Idempotente: reusa la subscription existente.
 */
export async function activarPush(): Promise<ActivarResultado> {
  // Apenas empieza, antes de cualquier espera (el permiso, el service worker, la fila). "Desactivar"
  // GANA siempre (review #157): si arranca mientras este "Activar" espera —el permiso del navegador
  // puede tardar lo que la persona tarde en contestar—, este se cancela en el próximo paso y deshace
  // lo que haya creado, en vez de dejar el buzón vivo después de la baja.
  const version = versionDelRegistro;
  const seDesactivo = () => version !== versionDelRegistro;
  const cancelado: ActivarResultado = { ok: false, motivo: 'cancelado' };
  const soporte = soportePush();
  if (soporte !== 'ok') return { ok: false, motivo: soporte };

  // Con su propio try: esta función promete devolver el error en vez de tirarlo —el
  // resto del cuerpo ya lo hace— y `requestPermission` puede tirar (un navegador que
  // sólo acepta la forma vieja con callback, un contexto donde el permiso no se puede
  // pedir). Sin esto, la promesa rechazada sube a quien llamó y le deja el botón
  // colgado en "Activando…".
  //
  // No es `permiso-denegado`: nadie dijo que no, ni siquiera se llegó a preguntar.
  let permiso: NotificationPermission;
  try {
    permiso = await Notification.requestPermission();
  } catch (err) {
    return { ok: false, motivo: err instanceof Error ? err.message : 'error' };
  }
  if (permiso !== 'granted') return { ok: false, motivo: 'permiso-denegado' };
  if (seDesactivo()) return cancelado;

  let sub: PushSubscription;
  let eraNueva = false;
  try {
    const reg = await swReady();
    if (!reg) return { ok: false, motivo: 'sw-no-disponible' };
    const existente = await reg.pushManager.getSubscription();
    if (existente) {
      sub = existente;
    } else {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      eraNueva = true;
    }
  } catch (err) {
    return { ok: false, motivo: err instanceof Error ? err.message : 'error' };
  }
  if (seDesactivo()) {
    if (eraNueva) await sub.unsubscribe().catch(() => {});
    return cancelado;
  }

  // Registrar en el backend. Si falla y la subscription la creamos ACÁ, la revertimos: así el estado
  // no queda "activado" en falso (sub local sin registro en el backend → nunca llegarían pushes).
  // Si la subscription ya existía, NO la tocamos. Va por la fila, como todo registro (ver `enFila`).
  const { r, cuenta } = await enFila(async () => {
    // Ya en su turno: si se desactivó mientras esperaba, no sale. La baja ya salió (o sale atrás).
    if (seDesactivo()) return { r: cancelado, cuenta: null };
    const antes = await usuarioConSesion();
    const res = await registrarEnBackend(sub);
    // El pedido sale con la sesión del momento: sólo se sabe para qué cuenta quedó si era la misma
    // antes y después (review #157). Si cambió en el medio, no se anota nada y el próximo estado se
    // vuelve a averiguar.
    const despues = await usuarioConSesion();
    return { r: res, cuenta: antes !== null && antes === despues ? antes : null };
  });
  // Se desactivó mientras viajaba el registro: la baja de "Desactivar" sale por la fila DESPUÉS de
  // este pedido y lo borra del backend; acá sólo queda no darlo por bueno.
  if (r.ok && seDesactivo()) {
    if (eraNueva) await sub.unsubscribe().catch(() => {});
    return cancelado;
  }
  if (!r.ok && eraNueva) await sub.unsubscribe().catch(() => {});
  if (r.ok && cuenta) recordarRegistro(cuenta, 'registrado', version);
  return r;
}

// ── El encargado del registro ─────────────────────────────────────────────────────────────────
//
// El buzón del navegador se registra desde varios lados: al entrar una cuenta
// (`useSincronizarPush`), al mirar el estado (Configuración, el popup de anuncios) y al activar o
// desactivar. Pasan todos por acá, por dos razones (review de Greptile en #157):
//
// 1. **De a uno, en orden.** El backend deja el buzón a nombre del ÚLTIMO registro que le llega.
//    Si alguien cambia de cuenta A → B rápido y los dos pedidos salen juntos, el de A puede llegar
//    después y quedarse con el buzón. En fila, el de B sale recién cuando el de A terminó, y cada
//    pedido, antes de salir, se fija que su cuenta siga siendo la que tiene la sesión.
// 2. **Uno por cuenta y por carga de página.** Mirar el estado no tiene que volver a escribir lo
//    que ya se registró: se comparte el resultado. Un fallo NO se guarda: el próximo pedido
//    reintenta.

type EstadoRegistro = ResultadoSincronizar | 'pendiente';

let registro: { userId: string; estado: EstadoRegistro; promesa: Promise<ResultadoSincronizar> } | null = null;
let fila: Promise<unknown> = Promise.resolve();
/**
 * Sube cada vez que se olvida lo registrado (al desactivar). Un resultado que salió con una versión
 * vieja no se anota: un "Activar" que esperaba en la fila no puede volver a dejar "activadas"
 * después de "Desactivar" (review #157).
 */
let versionDelRegistro = 0;

/** Corre `tarea` cuando terminó la anterior. Una que falla no traba a las que siguen. */
function enFila<T>(tarea: () => Promise<T>): Promise<T> {
  const p = fila.then(tarea, tarea);
  fila = p.catch(() => {});
  return p;
}

async function usuarioConSesion(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

function recordarRegistro(userId: string, estado: ResultadoSincronizar, version: number): void {
  if (version !== versionDelRegistro) return; // se desactivó mientras tanto: ya no vale
  registro = { userId, estado, promesa: Promise.resolve(estado) };
}

/** Olvida lo registrado en esta página (al desactivar: el próximo estado se vuelve a averiguar). */
export function olvidarRegistroPush(): void {
  versionDelRegistro += 1;
  registro = null;
}

/**
 * Registra la subscription de este navegador a nombre de la cuenta con sesión. El backend guarda
 * una fila por `endpoint` y, si ya existía, se la PASA a esta cuenta: así es como el "buzón" del
 * navegador sigue a la cuenta que lo está usando.
 */
async function registrarEnBackend(sub: PushSubscription): Promise<ActivarResultado> {
  const json = sub.toJSON();
  if (!json.keys?.p256dh || !json.keys?.auth) return { ok: false, motivo: 'error' };
  try {
    const res = await atlasBackendFetch('/v1/push/subscribe', {
      endpoint: sub.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
    if (!res.ok) return { ok: false, motivo: `backend-${res.status}` };
  } catch (err) {
    return { ok: false, motivo: err instanceof Error ? err.message : 'backend-error' };
  }
  return { ok: true };
}

export type ResultadoSincronizar = 'registrado' | 'sin-suscripcion' | 'fallo';

/** Si el registro al entrar falla, se reintenta a los 10 s, al minuto y a los 5 minutos. */
export const REINTENTOS_PUSH_MS = [10_000, 60_000, 300_000];

/**
 * Cuánto esperar para reintentar el registro al entrar, o null si no hay que reintentar: salió
 * bien, no hay nada que registrar, entró otra cuenta o ya se agotaron los intentos.
 */
export function esperaParaReintentarPush(
  resultado: ResultadoSincronizar,
  intento: number,
  sigueLaMismaCuenta: boolean,
): number | null {
  if (resultado !== 'fallo' || !sigueLaMismaCuenta) return null;
  return REINTENTOS_PUSH_MS[intento] ?? null;
}

/**
 * Si este navegador ya tiene notificaciones activas, las vuelve a registrar a nombre de la cuenta
 * con sesión. No pide permiso ni crea nada: sólo re-registra lo que ya hay.
 *
 * Por qué hace falta: el navegador tiene UN solo "buzón" de push y el backend lo guardaba a nombre
 * de la cuenta que lo activó por primera vez. Al entrar con otra cuenta en el mismo navegador, la
 * pantalla decía "activadas" (mira sólo el navegador) pero el backend no tenía ningún dispositivo
 * para esa cuenta y sus avisos no llegaban a ningún lado (3/10, probado con dos cuentas en la misma
 * Mac). Ahora el buzón sigue a la última cuenta que entró en ese navegador: es a propósito, una
 * cuenta por navegador.
 *
 * Nunca lanza: un fallo acá no puede tocar el login ni la navegación.
 */
export async function sincronizarPush(): Promise<ResultadoSincronizar> {
  const userId = await usuarioConSesion();
  if (!userId) return 'sin-suscripcion';
  // Ya registrado (o en camino) para esta cuenta: se comparte. Entre el chequeo y la asignación no
  // hay ningún `await`, así que dos llamadas simultáneas no pueden lanzar dos registros.
  if (registro?.userId === userId && registro.estado !== 'fallo') return registro.promesa;

  const actual: { userId: string; estado: EstadoRegistro; promesa: Promise<ResultadoSincronizar> } = {
    userId,
    estado: 'pendiente',
    promesa: Promise.resolve<ResultadoSincronizar>('fallo'),
  };
  actual.promesa = enFila(() => registrarSiSigueSiendo(userId)).then((r) => {
    actual.estado = r;
    return r;
  });
  registro = actual;
  return actual.promesa;
}

async function registrarSiSigueSiendo(userId: string): Promise<ResultadoSincronizar> {
  try {
    if (soportePush() !== 'ok' || Notification.permission !== 'granted') return 'sin-suscripcion';
    // Mientras esperaba en la fila pudo entrar otra cuenta: este pedido ya no corresponde, y el de
    // la cuenta nueva viene atrás. Mandarlo le devolvería el buzón a la cuenta que se fue.
    if ((await usuarioConSesion()) !== userId) return 'fallo';
    const reg = await swReady();
    if (!reg) return 'sin-suscripcion';
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return 'sin-suscripcion';
    if (!(await registrarEnBackend(sub)).ok) return 'fallo';
    // Y después: si la cuenta cambió mientras el pedido viajaba, no se sabe a nombre de quién quedó.
    return (await usuarioConSesion()) === userId ? 'registrado' : 'fallo';
  } catch {
    return 'fallo';
  }
}

/**
 * ¿Hay que re-registrar el push por este cambio de sesión? Sí cuando entra una cuenta que esta
 * página todavía no registró: la sesión que ya estaba al abrir (`INITIAL_SESSION`), un login
 * (`SIGNED_IN`) o un cambio de cuenta. No en cada `SIGNED_IN` repetido de la misma cuenta
 * (supabase-js lo vuelve a disparar, por ejemplo al volver a la pestaña): sería un pedido de más.
 */
export function debeSincronizarPush(
  evento: string,
  usuarioYaSincronizado: string | null,
  usuarioNuevo: string | null,
): boolean {
  if (!usuarioNuevo) return false;
  if (evento !== 'INITIAL_SESSION' && evento !== 'SIGNED_IN') return false;
  return usuarioNuevo !== usuarioYaSincronizado;
}

/** Da de baja la subscription local y avisa al backend (best-effort). */
export async function desactivarPush(): Promise<void> {
  olvidarRegistroPush();
  try {
    const reg = await swReady();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => {});
    // Por la fila: un registro que estaba en camino no puede llegar DESPUÉS y revivir el buzón.
    await enFila(() => atlasBackendFetch('/v1/push/unsubscribe', { endpoint })).catch(() => {});
  } catch {
    /* best-effort */
  } finally {
    // Otra vez al terminar: un registro que se pidió MIENTRAS se desactivaba (la pantalla mirando el
    // estado) pudo anotar "registrado" antes de la baja. El próximo estado se vuelve a averiguar.
    olvidarRegistroPush();
  }
}

/**
 * ¿Le llegan los avisos a ESTA cuenta en este dispositivo? (para reflejar el estado en la UI)
 *
 * No alcanza con que el navegador tenga una subscription: puede estar registrada a nombre de otra
 * cuenta. Sólo cuenta como activa si el backend confirmó el registro para esta cuenta; si no, la
 * pantalla ofrece "Activar" en vez de decir que ya está. Si ya se registró en esta página, no vuelve
 * a escribir: comparte ese resultado.
 */
export async function estaSuscripto(): Promise<boolean> {
  return (await sincronizarPush()) === 'registrado';
}
